import { useEffect, useId, useState } from "react";
import { AlertCircle, Loader2, MailCheck, Paperclip, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { uploadAttachment } from "@/api/attachments";
import { uploadComplaintAttachment } from "@/api/complaints";
import { useAddSupplierResponse } from "@/hooks/useSuppliers";
import type { SupplierResponseResult } from "@/api/suppliers";

/**
 * D-133 E-W2 — «Додати відповідь постачальника» (manual, P0).
 *
 * A human records the supplier's reply: date · text · optional position/result · optional
 * next recommended action · optional files. Files reuse the proven typed-upload path
 * (uploadAttachment → uploadComplaintAttachment with attachmentType `supplier_response`,
 * i.e. the same magic-byte + sha256 + size validation as evidence). On submit the backend
 * appends a `supplier_response` timeline event, flips supplierStatus → replied (stage
 * «Отримано відповідь постачальника»), and notifies the author/responsible.
 *
 * 🔴 NO auto inbound-email matching — that is P1 (deferred).
 */

const MAX_FILE_BYTES = 60 * 1024 * 1024; // backend caps: 20MB doc/img · 60MB mp4/mov
const FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,.tif,.tiff,.doc,.docx,.mp4,.mov";

function errMsg(e: unknown): string {
  return (e as Error)?.message ?? "Сталася помилка";
}

function mb(bytes: number): string {
  return (bytes / 1048576).toFixed(1);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

interface PendingFile {
  attachmentId: string;
  fileName: string;
  size: number;
}

export function SupplierResponseModal({
  complaintId,
  supplierName,
  open,
  onClose,
  onAdded,
}: {
  complaintId: string;
  supplierName?: string | null;
  open: boolean;
  onClose: () => void;
  onAdded: (result: SupplierResponseResult) => void;
}) {
  const headingId = useId();
  const mut = useAddSupplierResponse(complaintId);

  const [responseDate, setResponseDate] = useState(today());
  const [text, setText] = useState("");
  const [position, setPosition] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // reset on (re)open
  useEffect(() => {
    if (!open) return;
    setResponseDate(today());
    setText("");
    setPosition("");
    setNextAction("");
    setFiles([]);
    setUploading(false);
    setError(null);
  }, [open]);

  if (!open) return null;

  async function pickFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      // SEQUENTIAL (mobile memory) — native upload → typed supplier_response row.
      for (const f of Array.from(list)) {
        if (f.size > MAX_FILE_BYTES) {
          setError(`«${f.name}» перевищує ${mb(MAX_FILE_BYTES)} МБ`);
          continue;
        }
        const native = await uploadAttachment(f, "CClientComplaintAttachment", "file");
        const typed = await uploadComplaintAttachment(complaintId, {
          fileId: native.id,
          attachmentType: "supplier_response",
        });
        setFiles((prev) => [...prev, { attachmentId: typed.attachmentId, fileName: f.name, size: f.size }]);
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  const canSubmit = responseDate !== "" && text.trim() !== "" && !uploading && !mut.isPending;

  async function submit() {
    setError(null);
    try {
      const res = await mut.mutateAsync({
        responseDate,
        text: text.trim(),
        supplierPosition: position.trim() || undefined,
        nextAction: nextAction.trim() || undefined,
        attachmentIds: files.map((f) => f.attachmentId),
      });
      onAdded(res);
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      aria-hidden
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg border bg-background shadow-2xl"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h4 id={headingId} className="flex items-center gap-2 text-sm font-semibold">
            <MailCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
            Відповідь постачальника
            {supplierName ? (
              <span className="text-xs font-normal text-muted-foreground">· {supplierName}</span>
            ) : null}
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {/* response date */}
          <section className="space-y-1.5">
            <label
              htmlFor={`${headingId}-date`}
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Дата відповіді
            </label>
            <input
              id={`${headingId}-date`}
              type="date"
              value={responseDate}
              max={today()}
              onChange={(e) => setResponseDate(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </section>

          {/* reply text */}
          <section className="space-y-1.5">
            <label
              htmlFor={`${headingId}-text`}
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Текст відповіді
            </label>
            <textarea
              id={`${headingId}-text`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder="Що відповів постачальник…"
              className="w-full resize-y rounded-md border bg-background px-3 py-2 font-sans text-[13px] text-foreground/90"
            />
          </section>

          {/* supplier position (optional) */}
          <section className="space-y-1.5">
            <label
              htmlFor={`${headingId}-pos`}
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Позиція / результат постачальника <span className="font-normal normal-case">(необовʼязково)</span>
            </label>
            <input
              id={`${headingId}-pos`}
              type="text"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="напр. визнав брак / відхилив / запросив зразок"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </section>

          {/* next recommended action (optional) */}
          <section className="space-y-1.5">
            <label
              htmlFor={`${headingId}-next`}
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Наступна рекомендована дія <span className="font-normal normal-case">(необовʼязково)</span>
            </label>
            <input
              id={`${headingId}-next`}
              type="text"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              placeholder="напр. оформити заміну / закрити рекламацію"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </section>

          {/* files (optional) */}
          <section className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Paperclip className="h-3.5 w-3.5" aria-hidden /> Файли постачальника{" "}
              <span className="font-normal normal-case">(необовʼязково)</span>
            </label>
            {files.length > 0 && (
              <ul className="space-y-1">
                {files.map((f) => (
                  <li
                    key={f.attachmentId}
                    className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5 text-[12.5px]"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {f.fileName} <span className="text-muted-foreground">({mb(f.size)} МБ)</span>
                    </span>
                    <button
                      type="button"
                      aria-label={`Прибрати ${f.fileName}`}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => setFiles((prev) => prev.filter((p) => p.attachmentId !== f.attachmentId))}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label
              className={cn(
                "inline-flex min-h-[44px] cursor-pointer items-center gap-1.5 rounded-md border px-3 py-2 text-sm text-muted-foreground hover:text-foreground",
                uploading && "pointer-events-none opacity-50",
              )}
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              Додати файл
              <input
                type="file"
                multiple
                accept={FILE_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  void pickFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
            <p className="text-[11px] text-muted-foreground">
              PDF, фото, документи (≤20 МБ) або відео mp4/mov (≤60 МБ).
            </p>
          </section>

          {error && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button size="sm" onClick={submit} disabled={!canSubmit} className="gap-1.5">
            {mut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <MailCheck className="h-4 w-4" aria-hidden />
            )}
            Зберегти відповідь
          </Button>
        </div>
      </div>
    </div>
  );
}
